/**
 * `/plans/nowy` on an empty store — after a reload, a crash, a closed tab — and what this device
 * kept of the unsaved plan: the answer, and the two acts on it.
 *
 * The page owns the notice this is drawn in (`pages/plan-builder`'s `BuilderNotice`); this hook
 * decides what it says, and `LeftoverPlanAnswers` draws the plan and the two answers inside it.
 *
 * ── Decided before the first paint, and only for a KNOWN user ────────────────────────────────
 * The record is read synchronously during render the first time a user is known, so the screen
 * never draws the "gone" notice and then swaps in the question. A degraded boot has a session whose
 * user is not confirmed yet: the answer is `pending` until it is, because "nothing to restore" said
 * for nobody would be a guess that stays on screen. Reading removes nothing; a broken record is
 * swept from an effect.
 *
 * ── Restoring is a load, and nothing more ────────────────────────────────────────────────────
 * `restore` puts the kept tree into the document store as an unsaved seed; the builder's document
 * hook sees an unsaved plan and renders it. The record is not cleared: it goes on mirroring the
 * plan until the first Zapisz. `remove` deletes the offered plan from the device and answers
 * `nothing` from then on.
 */

import { useEffect, useState } from 'react';

import {
  discardLeftoverPlan,
  readNewPlanEntry,
  resumeLeftoverPlan,
  sweepUnsavedPlanRecord,
  type LeftoverPlan,
  type NewPlanEntry,
} from '~/entities/plan-draft';
import { useSessionUser } from '~/entities/session';

export type UnsavedPlanRecovery =
  | { readonly status: 'pending' }
  | { readonly status: 'nothing' }
  | {
      readonly status: 'offer';
      readonly plan: LeftoverPlan;
      readonly restore: () => void;
      readonly remove: () => void;
    };

export function useUnsavedPlanRecovery(): UnsavedPlanRecovery {
  const userId = useSessionUser()?.id ?? null;
  const [offer, setOffer] = useState<{
    readonly userId: string;
    readonly entry: NewPlanEntry;
  } | null>(null);
  if (userId !== null && offer?.userId !== userId) {
    setOffer({ userId, entry: readNewPlanEntry(userId) });
  }
  useEffect(() => {
    if (userId !== null) sweepUnsavedPlanRecord(userId);
  }, [userId]);

  if (offer === null || offer.userId !== userId) return { status: 'pending' };
  const { entry } = offer;
  if (entry.kind === 'create') return { status: 'nothing' };
  return {
    status: 'offer',
    plan: entry.plan,
    restore: () => {
      resumeLeftoverPlan(entry.plan, offer.userId);
    },
    remove: () => {
      discardLeftoverPlan(entry.plan, offer.userId);
      setOffer({ userId: offer.userId, entry: { kind: 'create' } });
    },
  };
}
